import { useState } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
  useTheme,
  Button,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import Box from '@mui/material/Box'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { ArrowDownwardRounded, ArrowUpwardRounded } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import FsModuleSummary from '../../components/fs-module-summary/fs-module-summary'
import { moduleConfigMap } from '../../shared/constants'
import FsFullPageLoader from '../../components/common/atoms/FullPageLoader'
import { useAppSelector } from '../../store/hooks'
import { useLoading } from '../../shared/hooks'
import ACTIONS from '../../store/constants'
import { getIcon } from '../../components/common/Icons'
import FsLeftInfoDrawer from '../../components/common/atoms/LeftInfoDrawer'
import AnimatedProgressBar from '../../components/common/molecules/AnimatedProgressBar'

const { ANALYSIS } = ACTIONS

const FsOverview = () => {
  const { currentLanguage } = useAppSelector((state) => state.currentUser)
  const { modulesSummary, modulesInfo } = useAppSelector((state) => state.currentAnalysis)
  const [detailedView, setDetailedView] = useState(false)
  const theme = useTheme()
  const { t, i18n } = useTranslation()

  const [helpOpen, setHelpOpen] = useState(false)
  const handleHelpOpen = () => setHelpOpen(true)
  const handleHelpClose = () => setHelpOpen(false)

  const curSummary = modulesInfo
    ? modulesInfo.filter((info) => info.locale === i18n.language)[0]
    : {}
  const helpTextHtml = curSummary.summary ?? ''
  const helpTextHtml1 = curSummary.improvementScope ?? ''

  const { isFetching } = useLoading([ANALYSIS.SET_MODULES_INFO])

  const noOfModules = modulesSummary?.length ?? 0

  return (
    <>
      <Stack mb={1}>
        <Stack direction="row" alignItems="center">
          {getIcon('overview')}
          <Typography variant="h1" ml={1}>
            {t('overview')}
          </Typography>
          <Box
            pl={1}
            pt={1}
            onClick={handleHelpOpen}
            sx={{ cursor: 'pointer', color: theme.palette.text.secondary }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>
          {noOfModules !== 1 && (
            <FormControlLabel
              control={
                <Switch
                  checked={detailedView}
                  onChange={() => setDetailedView(!detailedView)}
                  name="gilad"
                />
              }
              label={t('show.compare.groups')}
              sx={{ ml: 0.5 }}
              className="comparegroups"
            />
          )}
        </Stack>
      </Stack>
      {!isFetching && modulesSummary && (
        <Grid
          container
          display="flex"
          alignItems="stretch"
          justifyContent="flex-start"
          spacing={4}
          p={1}
          sx={{ overflowY: 'auto', height: 'calc(100vh - 170px)' }}
        >
          {modulesSummary?.map((moduleSummary, index) => {
            const moduleConfig = moduleConfigMap.get(moduleSummary.moduleName)
            const mainColor = moduleConfig?.fsColor?.colors[1] || theme.palette.primary.main
            const diff =
              Math.round((Number(moduleSummary.fairnessAchieved) + Number.EPSILON) * 10) / 10 -
              Math.round((Number(moduleSummary.previousRunFairness ?? 0) + Number.EPSILON) * 10) /
                10
            return (
              <Grid
                size={{ sm: 12, md: 6, lg: noOfModules === 1 ? 9 : 3 }}
                key={moduleSummary.moduleName}
                mb={4}
              >
                <div className={`${moduleSummary.moduleName}module walkthroughmodule${index}`}>
                  <Card
                    elevation={1}
                    sx={{
                      boxShadow: `2px 2px 8px 2px ${mainColor}66`,
                      // border: `1px solid ${mainColor}66`,
                      borderRadius: 3,
                    }}
                  >
                    <CardHeader
                      title={t(`overview.heading.${moduleSummary.moduleName}` as any)}
                      slotProps={{ title: { variant: 'h2', className: 'font-semibold' } }}
                      avatar={getIcon(moduleSummary.moduleName, {
                        color: moduleConfigMap.get(moduleSummary.moduleName)?.fsColor[
                          theme.palette.mode === 'light' ? 'primary' : 'light'
                        ],
                      })}
                    />
                    <CardContent sx={{ alignContent: 'center' }}>
                      <Typography variant="h3" className="-mt-3">
                        Fairness {t('achieved')}
                      </Typography>
                      <Typography variant="n1" className="text-center mt-4">
                        {Number(moduleSummary.fairnessAchieved).toLocaleString(currentLanguage, {
                          maximumFractionDigits: 1,
                          minimumFractionDigits: 0,
                        })}{' '}
                        %
                      </Typography>

                      <Grid
                        size={{ xs: 12 }}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius={2}
                        mx="auto"
                        my={2}
                        width="60%"
                        minWidth={200}
                        maxWidth={325}
                      >
                        <AnimatedProgressBar
                          progress={
                            moduleSummary.fairnessAchieved > 0 ? moduleSummary.fairnessAchieved : 0
                          }
                          color={moduleConfig?.fsColor?.light ?? ''}
                        />
                      </Grid>

                      {moduleSummary.previousRunFairness && (
                        <Stack alignItems="center">
                          <Stack
                            direction="row"
                            alignItems="flex-start"
                            justifyContent="center"
                            width="80%"
                          >
                            <Typography variant="body2" textAlign="center" ml={0.25} mt={-0.25}>
                              {diff > 0 ? (
                                <ArrowUpwardRounded
                                  sx={{ fontSize: 16, mb: '-4px' }}
                                  color="success"
                                />
                              ) : diff < 0 ? (
                                <ArrowDownwardRounded
                                  sx={{ fontSize: 16, mb: '-4px' }}
                                  color="error"
                                />
                              ) : null}
                              {diff !== 0
                                ? ` ${diff > 0 ? '+' : '-'}${Math.abs(diff).toLocaleString(
                                    currentLanguage,
                                    { maximumFractionDigits: 1 },
                                  )} % ${t('compare.to.prev.data')}`
                                : t('no.compare.to.prev.data')}
                            </Typography>
                          </Stack>
                        </Stack>
                      )}

                      <FsModuleSummary
                        moduleSummary={moduleSummary}
                        moduleConfig={moduleConfigMap.get(moduleSummary.moduleName)}
                        detailedView={detailedView}
                        noOfModules={noOfModules}
                      />
                    </CardContent>
                  </Card>
                </div>
              </Grid>
            )
          })}
          {/* @ts-ignore */}
          {noOfModules !== 4 && (
            <Grid size={{ sm: 6, md: 6, lg: 3 }} key="more_module" mb={4}>
              <Box className="flex w-full">
                <Card
                  elevation={1}
                  sx={{
                    boxShadow: `2px 2px 8px 2px ${theme.palette.primary.main}66`,
                  }}
                  className="w-full h-full"
                >
                  <CardContent className="flex flex-col justify-center h-full mx-4">
                    <Typography variant="h6" className="font-bold mt-3 text-center">
                      {currentLanguage === 'de'
                        ? 'Entdecken Sie unsere vollständige und detaillierte Analyse'
                        : 'Discover our complete and detailed Analysis'}
                    </Typography>
                    {currentLanguage === 'de' ? (
                      <Typography
                        className="mt-2"
                        component="div"
                        style={{ whiteSpace: 'pre-line' }}
                      >
                        FAIR_solution ist eine ganzheitliche und präzise People Analytics Software,
                        die <strong>4 Handlungsfelder</strong> analysiert:
                        <ul>
                          <li>Lohn</li>
                          <li>Karriere</li>
                          <li>Arbeitszeit</li>
                          <li>Weiterbildung</li>
                        </ul>
                        Um zu erfahren, welche Mitarbeitenden Gruppen von einer möglichen
                        Ungleichbehandlung betroffen sind, bietet die Software{' '}
                        <strong>4 Diversitätsdimensionen</strong>:
                        <ul>
                          <li>Geschlecht/Gender</li>
                          <li>Behinderung</li>
                          <li>Herkunft</li>
                          <li>Alter</li>
                        </ul>
                      </Typography>
                    ) : (
                      <Typography
                        className="mt-8"
                        component="div"
                        style={{ whiteSpace: 'pre-line' }}
                      >
                        FAIR_solution is a complete and precise people analytics software that
                        analyzes <strong>4 Modules</strong>:
                        <ul>
                          <li>Pay</li>
                          <li>Career</li>
                          <li>Working hours</li>
                          <li>Training</li>
                        </ul>
                        To find out which groups of employees are affected by possible unequal
                        treatment, the software offers <strong>4 Diversity Dimensions</strong>:
                        <ul>
                          <li>Gender</li>
                          <li>Disability</li>
                          <li>Origin</li>
                          <li>Age</li>
                        </ul>
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      endIcon={<OpenInNewIcon />}
                      href={`https://www.ines-analytics.com/${
                        currentLanguage === 'en' ? 'en/' : ''
                      }fair-solution`}
                      target="_blank"
                      sx={{ mt: 1, mb: 2 }}
                    >
                      {t('read.more.ines')}
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
      {isFetching && <FsFullPageLoader />}
      <FsLeftInfoDrawer open={helpOpen} onClose={handleHelpClose}>
        <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
          {helpTextHtml}
        </Typography>
        <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
          {`\n${helpTextHtml1}`}
        </Typography>
      </FsLeftInfoDrawer>
    </>
  )
}

export default FsOverview
